import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default:
          'bg-card/80 text-foreground border-border/50 shadow-[inset_0_0_20px_rgba(34,211,238,0.03)] [&>svg]:text-biolume-cyan',
        destructive:
          'bg-red-950/30 text-red-300 border-red-500/30 shadow-[inset_0_0_20px_rgba(239,68,68,0.05),0_0_15px_rgba(239,68,68,0.1)] [&>svg]:text-red-400',
        success:
          'bg-green-950/30 text-green-300 border-green-500/30 shadow-[inset_0_0_20px_rgba(34,197,94,0.05)] [&>svg]:text-green-400',
        warning:
          'bg-yellow-950/30 text-yellow-300 border-yellow-500/30 shadow-[inset_0_0_20px_rgba(234,179,8,0.05)] [&>svg]:text-yellow-400',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm opacity-90 [&_p]:leading-relaxed', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
